export interface AnalyzeRequestInput {
  requestName: string;
  hasCollection: boolean;
  hasEnvironment: boolean;
  path: string;
  queryParams: Array<{ key?: string; value?: string }>;
  headers: Array<{ key?: string; value?: string }>;
  rawBody: string;
  bodyType: string;
  prompt?: (message?: string, defaultValue?: string) => string | null;
}

export interface AnalyzeRequestResult {
  issues: string[];
  appliedChanges: string[];
  updates: {
    path?: string;
    body?: string;
  };
}

export function formatAnalyzeRequestMessage(
  result: AnalyzeRequestResult,
  options?: { requestLabel?: string; successPrefix?: string }
): { text: string; type: 'success' | 'error' } {
  const { issues, appliedChanges } = result;
  const requestLabel = options?.requestLabel;
  const successPrefix =
    options?.successPrefix ??
    (requestLabel
      ? `Analyze Request for "${requestLabel}": no obvious issues found with basic validation. You can proceed to Send.`
      : 'Analyze Request: no obvious issues found with basic validation. You can proceed to Send.');

  if (!issues.length && !appliedChanges.length) {
    return { text: successPrefix, type: 'success' };
  }

  const parts: string[] = [];
  if (appliedChanges.length) {
    parts.push(`Applied fixes:\n- ${appliedChanges.join('\n- ')}`);
  }
  if (issues.length) {
    parts.push(`Remaining issues:\n- ${issues.join('\n- ')}`);
  }

  const prefix = requestLabel ? `Analysis for "${requestLabel}":\n\n` : '';
  return {
    text: `${prefix}${parts.join('\n\n')}`,
    type: issues.length ? 'error' : 'success',
  };
}

export function analyzeRequest(input: AnalyzeRequestInput): AnalyzeRequestResult {
  const issues: string[] = [];
  const appliedChanges: string[] = [];
  const updates: AnalyzeRequestResult['updates'] = {};
  const promptFn = input.prompt ?? (typeof window !== 'undefined' ? window.prompt.bind(window) : undefined);

  const name = input.requestName?.trim() || '';
  if (!name) {
    issues.push('Request name is missing.');
  }
  if (!input.hasCollection) {
    issues.push('Collection is not selected.');
  }
  if (!input.hasEnvironment) {
    issues.push('Environment is not selected.');
  }

  let path = input.path?.trim() ?? '';
  if (!path) {
    issues.push('Request path is empty.');
  } else if (!path.startsWith('/')) {
    issues.push(`Request path "${path}" should start with "/".`);
  }

  const pathParamMatches = Array.from(path.matchAll(/\{([^}]+)\}/g));
  if (pathParamMatches.length > 0) {
    const paramNames = pathParamMatches.map((m) => m[1]);
    paramNames.forEach((pName) => {
      if (pName.toLowerCase() === 'email') {
        const answer = promptFn?.(
          `Path parameter "{${pName}}" is required. Please provide a valid email:`,
          ''
        );
        if (answer && answer.trim()) {
          const trimmed = answer.trim();
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(trimmed)) {
            issues.push(`Provided email "${trimmed}" for path parameter "{${pName}}" does not look valid.`);
          } else {
            const newPath = path.replace(`{${pName}}`, encodeURIComponent(trimmed));
            if (newPath !== path) {
              appliedChanges.push(`Updated path parameter "{${pName}}" to "${trimmed}".`);
              path = newPath;
              updates.path = newPath;
            }
          }
        } else {
          issues.push(`Path parameter "{${pName}}" is required but no value was provided.`);
        }
      }
    });

    const unresolved = Array.from(path.matchAll(/\{([^}]+)\}/g)).map((m) => m[1]);
    if (unresolved.length > 0) {
      issues.push(
        `Path contains unresolved parameters: ${unresolved.join(
          ', '
        )}. Make sure they are substituted before execution.`
      );
    }
  }

  const placeholderRegex = /(sample_|test_|dummy|lorem|xxx)/i;

  input.queryParams.forEach((qp) => {
    if (qp.value && placeholderRegex.test(String(qp.value))) {
      issues.push(`Query param "${qp.key}" appears to use a placeholder value: "${qp.value}".`);
    }
  });

  input.headers.forEach((h) => {
    if (h.value && placeholderRegex.test(String(h.value))) {
      issues.push(`Header "${h.key}" appears to use a placeholder value: "${h.value}".`);
    }
  });

  let bodyToCheck = input.rawBody ? String(input.rawBody) : '';
  if (input.bodyType !== 'none' && bodyToCheck) {
    if (placeholderRegex.test(bodyToCheck)) {
      issues.push(
        'Request body appears to contain placeholder values (sample_ / test_ / dummy / lorem / xxx).'
      );
    }
    try {
      const parsed = JSON.parse(bodyToCheck);
      if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'country')) {
        const countryVal = (parsed as { country?: unknown }).country;
        if (
          typeof countryVal === 'string' &&
          (countryVal.toLowerCase() === 'sample_country' ||
            countryVal.length < 2 ||
            !/^[a-zA-Z\s]+$/.test(countryVal))
        ) {
          const answer = promptFn?.(
            `Field "country" has a placeholder or unlikely value "${countryVal}". Please provide a real country name:`,
            ''
          );
          if (answer && answer.trim()) {
            const trimmed = answer.trim();
            (parsed as { country: string }).country = trimmed;
            const newBody = JSON.stringify(parsed, null, 2);
            bodyToCheck = newBody;
            updates.body = newBody;
            appliedChanges.push(`Updated body field "country" from "${countryVal}" to "${trimmed}".`);
          } else {
            issues.push('Body field "country" looks invalid and no replacement was provided.');
          }
        }
      }
    } catch {
      // If JSON is invalid we only keep placeholder heuristic
    }
  }

  return { issues, appliedChanges, updates };
}
